import React from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import './call-logs-prospect-drawer-header-skeleton.scss';

export interface CallLogsProspectDrawerHeaderSkeletonProps {
  /** Passed through to each `SkeletonLoading` instance. */
  enableAnimation?: boolean;
}

/**
 * Skeleton for the call-logs prospect drawer (Figma: Dialer → Field Selection loading).
 * Mirrors layout: header strip, profile row, attribute rows, note editor, tabs, activity card, footer.
 */
const CallLogsProspectDrawerHeaderSkeleton: React.FC<CallLogsProspectDrawerHeaderSkeletonProps> = ({
  enableAnimation = true,
}) => {
  const sProps = { enableAnimation };

  return (
    <div className="bs-call-logs-prospect-drawer-skeleton">
      <div className="bs-call-logs-prospect-drawer-skeleton__top-head">
        <SkeletonLoading width={40} height={40} {...sProps} />
        <div className="bs-call-logs-prospect-drawer-skeleton__top-head-text">
          <SkeletonLoading width={97} height={20} {...sProps} />
          <SkeletonLoading width={165} height={20} {...sProps} />
        </div>
      </div>

      <div className="bs-call-logs-prospect-drawer-skeleton__scroll">
        <div className="bs-call-logs-prospect-drawer-skeleton__section bs-call-logs-prospect-drawer-skeleton__section--profile">
          <div className="bs-call-logs-prospect-drawer-skeleton__profile-row">
            <SkeletonLoading width={64} height={64} circle {...sProps} />
            <div className="bs-call-logs-prospect-drawer-skeleton__profile-meta">
              <div className="bs-call-logs-prospect-drawer-skeleton__profile-lines">
                <SkeletonLoading width={161} height={20} {...sProps} />
                <SkeletonLoading width={97} height={14} {...sProps} />
              </div>
              <div className="bs-call-logs-prospect-drawer-skeleton__social-dots">
                <SkeletonLoading width={18} height={18} circle {...sProps} />
                <SkeletonLoading width={18} height={18} circle {...sProps} />
                <SkeletonLoading width={18} height={18} circle {...sProps} />
              </div>
            </div>
          </div>
        </div>

        <div className="bs-call-logs-prospect-drawer-skeleton__section bs-call-logs-prospect-drawer-skeleton__section--fields">
          <div className="bs-call-logs-prospect-drawer-skeleton__field-group">
            <SkeletonLoading width={103} height={14} {...sProps} />
            <div className="bs-call-logs-prospect-drawer-skeleton__bar-full">
              <SkeletonLoading width={267} height={14} {...sProps} />
            </div>
          </div>
          <div className="bs-call-logs-prospect-drawer-skeleton__field-group">
            <SkeletonLoading width={103} height={14} {...sProps} />
            <div className="bs-call-logs-prospect-drawer-skeleton__bar-full">
              <SkeletonLoading width={267} height={14} {...sProps} />
            </div>
          </div>
        </div>

        <div className="bs-call-logs-prospect-drawer-skeleton__section">
          <SkeletonLoading width={103} height={14} {...sProps} />
          <div className="bs-call-logs-prospect-drawer-skeleton__bar-full">
            <SkeletonLoading width="100%" height={32} {...sProps} />
          </div>
        </div>

        <div className="bs-call-logs-prospect-drawer-skeleton__note-card">
          <div className="bs-call-logs-prospect-drawer-skeleton__note-label">
            <SkeletonLoading width={103} height={14} {...sProps} />
          </div>
          <div className="bs-call-logs-prospect-drawer-skeleton__note-editor">
            <div className="bs-call-logs-prospect-drawer-skeleton__note-editor-body">
              <SkeletonLoading width={240} height={14} {...sProps} />
            </div>
            <div className="bs-call-logs-prospect-drawer-skeleton__note-editor-footer">
              <div className="bs-call-logs-prospect-drawer-skeleton__note-editor-footer-left">
                <SkeletonLoading width={32} height={32} circle {...sProps} />
                <SkeletonLoading width={32} height={32} circle {...sProps} />
              </div>
              <SkeletonLoading width={82} height={32} {...sProps} />
            </div>
          </div>
        </div>

        <div className="bs-call-logs-prospect-drawer-skeleton__tabs">
          <SkeletonLoading width={54} height={14} {...sProps} />
          <SkeletonLoading width={54} height={14} {...sProps} />
        </div>

        <div className="bs-call-logs-prospect-drawer-skeleton__activity-card">
          <div className="bs-call-logs-prospect-drawer-skeleton__activity-header">
            <div className="activity-header-line">
              <SkeletonLoading width={54} height={14} {...sProps} />
            </div>
            <div className="bs-call-logs-prospect-drawer-skeleton__bar-full">
              <SkeletonLoading width="100%" height={32} {...sProps} />
            </div>
          </div>
          <div className="bs-call-logs-prospect-drawer-skeleton__activity-list">
            <div className="bs-call-logs-prospect-drawer-skeleton__activity-item">
              <SkeletonLoading width={20} height={20} circle {...sProps} />
              <div className="bs-call-logs-prospect-drawer-skeleton__activity-item-lines">
                <div className="activity-item-line">
                  <SkeletonLoading width={123} height={18} {...sProps} />
                </div>
                <SkeletonLoading width={273} height={14} {...sProps} />
                <SkeletonLoading width={233} height={14} {...sProps} />
                <SkeletonLoading width={144} height={14} {...sProps} />
              </div>
            </div>
            <div className="bs-call-logs-prospect-drawer-skeleton__activity-item">
              <SkeletonLoading width={20} height={20} circle {...sProps} />
              <div className="bs-call-logs-prospect-drawer-skeleton__activity-item-lines">
                <div className="activity-item-line">
                  <SkeletonLoading width={123} height={18} {...sProps} />
                </div>
                <SkeletonLoading width={273} height={14} {...sProps} />
                <SkeletonLoading width={233} height={14} {...sProps} />
                <SkeletonLoading width={144} height={14} {...sProps} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bs-call-logs-prospect-drawer-skeleton__footer">
        <SkeletonLoading width={151} height={32} {...sProps} />
      </div>
    </div>
  );
};

export default CallLogsProspectDrawerHeaderSkeleton;
